import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Switch,
  TextField,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import { Flex, message, Table } from 'antd'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import * as Yup from 'yup'

import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import ImageIcon from '@mui/icons-material/Image'
import SaveIcon from '@mui/icons-material/Save'
import InputUpload from '../../../components/InputUpload'
import CategorySelectSearch from '../../../components/SelectSearch/CategorySelectSearch'
import ProductSelectSearch from '../../../components/SelectSearch/ProductSelectSearch'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { EPLATFORM } from '../../../constants/platform'
import useAxios, { axiosInstance } from '../../../hooks/useAxios'
import { useUser } from '../../../hooks/useUser'
import { ICategory } from '../../../types/category'
import { PaginationResponseInterface } from '../../../types/pagination'
import {
  ICollections,
  IPlatformLink,
  IProduct,
  IProductAttribute,
  IProductOption,
} from '../../../types/product'
import { isAllowTikTok } from '../../../constants/user'
import { validateMinMaxValue } from '../../../utils/validateMinMaxValue'
import ChannelSelectSearch from '../../../components/SelectSearch/ChannelSelectSearch'
import ChannelSelectMultipleSearch from '../../../components/SelectSearch/ChannelSelectMultipleSearch'

interface IProductFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  product?: IProduct & { isCopy?: boolean }
  onUpdateProductSuccess: () => void
}

interface IProductForm {
  code: string
  name: string
  description: string
  price: number | string
  cost: number | string
  weight?: string
  size?: string
  color?: string
  quantity: number
  commission: number
  category?: string
  tiktok?: {
    isTiktok?: boolean
    tiktokCategoryId?: string
    tiktokSizeChartImage?: string
    tiktokWeight?: number
  }
  referrer?: {
    tiktok?: string
    shopee?: string
  }
  platformLinks?: IPlatformLink[]
  channels?: {
    name: string
    objectId: string
    image: string
    code: string
  }[]
}

const getProductFormValidationSchema = () => {
  return Yup.object({
    code: Yup.string()
      .required('กรุณากรอกรหัสสินค้า')
      .min(1, 'รหัสสินค้าต้องมีอย่างน้อย 1 ตัวอักษร'),
    name: Yup.string()
      .required('กรุณากรอกชื่อสินค้า')
      .min(1, 'ชื่อสินค้าต้องมีอย่างน้อย 1 ตัวอักษร'),
    description: Yup.string(),
    price: Yup.number()
      .required('กรุณากรอกราคาสินค้า')
      .min(1, 'ราคาสินค้าต้องมีอย่างน้อย 1 บาท'),
    cost: Yup.number()
      .required('กรุณากรอกราคาทุน')
      .min(1, 'ราคาทุนต้องมีอย่างน้อย 1 บาท'),
    category: Yup.string().required('กรุณาเลือกหมวดหมู่สินค้า'),
    commission: Yup.number().min(
      0,
      'ค่าคอมมิชชั่นต้องมีค่ามากกว่าหรือเท่ากับ 0',
    ),
    weight: Yup.string().optional().nullable(),
    size: Yup.string().optional().nullable(),
    color: Yup.string().optional().nullable(),
  })
}

export const ProductFormModal = (props: IProductFormModalProps) => {
  const { isOpen, onClose, product, onUpdateProductSuccess } = props
  const user = useUser()
  const isUpdate = !!product?.objectId
  const [selectedImg, setSelectedImg] = useState<string | undefined>('')
  const [searchCategory, setSearchCategory] = useState<string>('')
  const [searchTiktokCategory, setSearchTiktokCategory] = useState<string>('')
  const [isAvailable, setIsAvailable] = useState<boolean>(true)
  const [searchProduct, setSearchProduct] = useState<string>('')
  const [collections, setCollections] = useState<ICollections[]>([])
  const [isCostCollections, setIsCostCollections] = useState<boolean>(true)
  const [options, setOptions] = useState<IProductOption[]>([])
  const [attributes, setAttributes] = useState<IProductAttribute[]>([])
  const [tiktokIsSizeChartRequired, setTiktokIsSizeChartRequired] =
    useState<boolean>(false)

  useEffect(() => {
    formik.resetForm()
    setSelectedImg(product?.image)
    setCollections(product?.collections ?? [])
    setIsAvailable(product?.isAvailable ?? false)
    setAttributes(product?.attributes ?? [])
    formik.setValues({
      code: product?.code || '',
      name: product?.name || '',
      description: product?.description || '',
      price: Number(product?.price) || 0,
      cost: Number(product?.cost) || 0,
      quantity: Number(product?.quantity) || 0,
      commission: Number(product?.commission) || 0,
      category: product?.productCategory?.objectId || '',
      weight: product?.meta?.weight || '',
      size: product?.meta?.size || '',
      color: product?.meta?.color || '',
      referrer: product?.referrer || {},
      platformLinks: product?.platformLinks || [],
      channels: product?.channels || [],
    })
  }, [isOpen])

  const formik = useFormik({
    initialValues: {
      code: '',
      name: '',
      description: '',
      price: 0,
      cost: 0,
      quantity: 0,
      commission: 0,
      tiktok: {
        isTiktok: false,
        tiktokCategoryId: '',
        tiktokSizeChartImage: '',
        tiktokWeight: 0,
      },
      referrer: {
        tiktok: '',
        shopee: '',
      },
      platformLinks: [] as IPlatformLink[],
    },
    validationSchema: getProductFormValidationSchema(),
    onSubmit: async (values: IProductForm) => {
      try {
        const platform = []
        if (values.tiktok?.isTiktok) {
          if (
            !values.tiktok?.tiktokCategoryId ||
            !values.tiktok?.tiktokWeight
          ) {
            return message.error('กรุณากรอกข้อมูล Tiktok ให้ครบถ้วน')
          }
          if (
            tiktokIsSizeChartRequired &&
            !values.tiktok?.tiktokSizeChartImage
          ) {
            return message.error('กรุณาอัพโหลดไซส์ชาร์ทของ Tiktok')
          }
          if (values.name.length < 25) {
            return message.error('ชื่อสินค้าต้องมีอย่างน้อย 25 ตัวอักษร')
          }
          platform.push(EPLATFORM.TIKTOK)
        }

        const reponse = await axiosInstance.request({
          method: isUpdate ? 'PUT' : 'POST',
          url: isUpdate ? `/product/${product?.objectId}` : '/product',
          data: {
            ...values,
            price: Number(values.price),
            commission: Number(values.commission),
            cost: Number(values.cost),
            image: selectedImg,
            isCollection: collections.length > 0 ? true : false,
            collections: collections.length > 0 ? collections : [],
            isAvailable: isAvailable,
            images: [selectedImg], //wait
            attributes: attributes,
            options: options.map((option) => ({
              name: option.name,
              image: !option.image ? selectedImg : option.image,
              size: option.size || '',
              weight: option.weight || '',
              color: option.color || '',
            })),
            // platform: platform,
            referrer: values.referrer,
            platformLinks: values.platformLinks || [],
            // tiktokCategoryId:
            //   values.tiktok?.tiktokCategoryId &&
            //   values.tiktok?.tiktokCategoryId.toString(),
            // tiktokSizeChartImage:
            //   values.tiktok?.tiktokSizeChartImage &&
            //   values.tiktok?.tiktokSizeChartImage.toString(),
            // tiktokWeight:
            //   values.tiktok?.tiktokWeight &&
            //   values.tiktok?.tiktokWeight.toString(),
          },
        })
        const { status } = reponse
        message.success(
          status === 201
            ? 'สร้างสินค้าสำเร็จ'
            : status === 200
              ? `แก้ไขสินค้า ${product?.name} สำเร็จ`
              : 'บันทึกข้อมูลไม่สำเร็จ',
        )
        setSelectedImg('')
        setOptions([])
        setCollections([])
        setIsAvailable(false)
        formik.resetForm()
        onClose(false)
        onUpdateProductSuccess()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
    },
  })

  const handleUploadClick = async (event: any) => {
    const file = event.target.files[0]
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await axiosInstance.post<any>('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.status !== 201) {
        return message.error('อัพโหลดรูปภาพไม่สำเร็จ')
      }
      setSelectedImg(response.data.url)
      message.success('อัพโหลดรูปภาพสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const tiktokSizeChartImagehandleUploadClick = async (event: any) => {
    const file = event.target.files[0]
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await axiosInstance.post<any>('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.status !== 201) {
        return message.error('อัพโหลดรูปภาพไม่สำเร็จ')
      }
      formik.setFieldValue('tiktok.tiktokSizeChartImage', response.data.url)
      message.success('อัพโหลดรูปภาพสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const categories = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: {
      page: 1,
      perPage: 5,
      search: searchCategory,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categories.mutate()
  }

  const handleAddCollection = async (newProduct: IProduct) => {
    setCollections([
      ...collections,
      {
        ...newProduct,
        quantity: 1,
      },
    ])
    calCollection(
      [
        ...collections,
        {
          ...newProduct,
          quantity: 1,
        },
      ],
      isCostCollections,
    )
  }

  const productList = useAxios<PaginationResponseInterface<IProduct>>({
    method: 'GET',
    url: '/product',
    params: {
      page: 1,
      perPage: 5,
      search: searchProduct,
      startDate: '',
      endDate: '',
    },
  })

  // const tikTokCategoriesList = useAxios<any>({
  //   method: 'GET',
  //   url: '/tiktok/categories',
  // })

  const handleSlectTiktokCategory = async (value: string) => {
    formik.setFieldValue('tiktok.tiktokCategoryId', value)
    try {
      const response = await axiosInstance.post<any>(
        `/tiktok/category-rule/${value}`,
        {
          merchant: user?.merchant?.prefix,
        },
      )
      setTiktokIsSizeChartRequired(response.data.isSizeChartRequired)
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const handleSearchTiktokCategory = async (value: string) => {
    setSearchTiktokCategory(value)
  }

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productList.mutate()
  }

  const calCollection = (collections: ICollections[], cal: boolean) => {
    // const price = collections.reduce(
    //   (acc, product: ICollections) => acc + (Number(product!.price) || 0),
    //   0,
    // )
    // const cost = collections.reduce(
    //   (acc, product: ICollections) => acc + (Number(product!.cost) || 0),
    //   0,
    // )
    // const quantity = collections.reduce(
    //   (acc, product: ICollections) => acc + (product!.quantity || 0),
    //   0,
    // )

    let price = 0,
      cost = 0,
      quantity = 0

    collections.map((product: ICollections) => {
      const qty = !cal ? 1 : Number(product.quantity ?? 0)
      price += Number(product.price ?? 0) * qty
      cost += Number(product.cost ?? 0) * qty
      quantity += Number(product.quantity ?? 0)
    })

    formik.setFieldValue('price', price)
    formik.setFieldValue('cost', cost)
  }

  const changeValueByIndexCollections = (
    index: number,
    updatedProperties: ICollections,
  ) => {
    console.log('updatedProperties', updatedProperties)
    setCollections((prevCollections) =>
      prevCollections.map((product, i) =>
        i === index ? { ...product, ...updatedProperties } : product,
      ),
    )
    calCollection(
      collections.map((product, i) =>
        i === index ? { ...product, ...updatedProperties } : product,
      ),
      isCostCollections,
    )
  }

  const handleSetCostCollections = (value: boolean) => {
    setIsCostCollections(value)
    calCollection(collections, value)
  }

  const handleDeleteCollectionByIndex = (index: number) => {
    setCollections((prevCollections) =>
      prevCollections.filter((_, i) => i !== index),
    )
    calCollection(
      collections.filter((_, i) => i !== index),
      isCostCollections,
    )
  }

  const changeValueByIndexOptions = (
    index: number,
    updatedProperties: IProductOption,
  ) => {
    setOptions((prevOptions) =>
      prevOptions.map((prevOption, i) =>
        i === index ? { ...prevOption, ...updatedProperties } : prevOption,
      ),
    )
  }

  const handleDeleteOptionByIndex = (index: number) => {
    console.log('index', index)
    setOptions((prevOption) => prevOption.filter((_, i) => i !== index))
  }

  const changeValueByIndexAttributes = (
    index: number,
    updatedProperties: IProductAttribute,
  ) => {
    setAttributes((prevAttributes) =>
      prevAttributes.map((prevAttribute, i) =>
        i === index
          ? { ...prevAttribute, ...updatedProperties }
          : prevAttribute,
      ),
    )
  }

  const handleDeleteAttributeByIndex = (index: number) => {
    setAttributes((prevAttribute) =>
      prevAttribute.filter((_, i) => i !== index),
    )
  }

  const handleUploadImg = async (index: number, event: any) => {
    const file = event.target.files[0]
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await axiosInstance.post<any>('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.status !== 201) {
        return message.error('อัพโหลดรูปภาพไม่สำเร็จ')
      }
      changeValueByIndexOptions(index, { image: response.data.url })
      message.success('อัพโหลดรูปภาพสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const columns = [
    {
      title: 'รายการสินค้า',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (_: undefined, product: ICollections) => {
        return (
          <Grid container alignItems={'center'}>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {product.image ? (
                  <img src={product.image} alt="Uploaded" width={`30%`} />
                ) : (
                  <Box>
                    <ImageIcon sx={{ fontSize: 35, color: '#ccc' }} />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Flex vertical>
                <Typography variant="subtitle1" color="text.secondary">
                  {product?.name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  รหัสสินค้า : {product?.code}
                </Typography>
              </Flex>
            </Grid>
          </Grid>
        )
      },
    },
    {
      title: 'ราคาขาย',
      dataIndex: 'price',
      key: 'price',
      width: 150,
      render: (price: number, product: ICollections, key: number) => (
        <TextField
          value={price}
          type={'number'}
          onKeyPress={(e) => {
            if (['+', '-', 'e', '*'].includes(e.key)) {
              e.preventDefault()
            }
          }}
          onBlur={(e) => {
            let n = e.target.value
            if (!n) {
              n = '0'
            }
            changeValueByIndexCollections(key, {
              price: n,
            })
          }}
          onChange={(e) => {
            changeValueByIndexCollections(key, {
              price: e.target.value,
            })
          }}
        />
      ),
    },
    {
      title: 'ต้นทุน',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      render: (cost: number, product: ICollections, key: number) => (
        <TextField
          value={cost}
          type={'number'}
          onKeyPress={(e) => {
            if (['+', '-', 'e', '*'].includes(e.key)) {
              e.preventDefault()
            }
          }}
          onBlur={(e) => {
            let n = e.target.value
            if (!n) {
              n = '1'
            }
            changeValueByIndexCollections(key, {
              cost: validateMinMaxValue(n, 1, 9999999) || undefined,
            })
          }}
          onChange={(e) => {
            changeValueByIndexCollections(key, {
              cost:
                validateMinMaxValue(e.target.value, 1, 9999999) || undefined,
            })
          }}
        />
      ),
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 170,
      render: (quantity: number, product: ICollections, key: number) => (
        <TextField
          value={quantity}
          type={'number'}
          onKeyPress={(e) => {
            if (['+', '-', 'e', '*', '.'].includes(e.key)) {
              e.preventDefault()
            }
          }}
          onBlur={(e) => {
            let n = e.target.value
            if (!n) {
              n = '1'
            }
            changeValueByIndexCollections(key, {
              quantity: validateMinMaxValue(n, 1, 9999999) || undefined,
            })
          }}
          onChange={(e) => {
            changeValueByIndexCollections(key, {
              quantity:
                validateMinMaxValue(e.target.value, 1, 9999999) || undefined,
            })
          }}
        />
      ),
    },
    {
      dataIndex: 'key',
      key: 'key',
      width: 100,
      render: (key: number) => (
        <Grid container justifyContent="center">
          <Button
            color="error"
            size="small"
            variant="contained"
            onClick={() => handleDeleteCollectionByIndex(key)}
          >
            <DeleteIcon />
          </Button>
          ,
        </Grid>
      ),
    },
  ]

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="lg">
      <DialogTitle>{product ? 'แก้ไขสินค้า' : 'เพิ่มสินค้าใหม่'}</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={12}>
                  <TextField
                    id="name"
                    fullWidth
                    label="กรอกชื่อสินค้า"
                    name="name"
                    helperText={
                      formik.touched.name && (formik.errors.name as string)
                    }
                    error={!!(formik.touched.name && formik.errors.name)}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.name}
                  />
                </Grid>
                <Grid item xs={12} lg={12}>
                  {/* <SelectSearch<any>
                    label={'เลือกหมวดหมู่สินค้า'}
                    onSelect={(e) =>
                      formik.setFieldValue('category', e?.value?.objectId)
                    }
                    search={searchCategory}
                    onSearch={handleSearchCategory}
                    defaultValue={
                      product
                        ? {
                            label: product?.productCategory?.name || '',
                            value: {
                              objectId:
                                product?.productCategory?.objectId || '',
                              name: product?.productCategory?.name || '',
                            },
                          }
                        : undefined
                    }
                    options={
                      categories.data?.records
                        .map(
                          (category: ICategory) =>
                            category.isAvailable && {
                              label: category.name,
                              value: category,
                            },
                        )
                        .reduce((acc: IOptionSelect<ICategory>[], category) => {
                          if (category) {
                            acc.push(category)
                          }
                          return acc
                        }, []) || ([] as IOptionSelect<ICategory>[])
                    }
                    name="category"
                    helperText={
                      formik.touched.category &&
                      (formik.errors.category as string)
                    }
                    error={
                      !!(formik.touched.category && formik.errors.category)
                    }
                    onBlur={formik.handleBlur}
                  /> */}

                  <CategorySelectSearch
                    defaultValue={
                      product
                        ? {
                            label: product?.productCategory?.name || '',
                            value: {
                              objectId:
                                product?.productCategory?.objectId || '',
                              name: product?.productCategory?.name || '',
                            },
                          }
                        : undefined
                    }
                    onSelect={(e) =>
                      formik.setFieldValue('category', e?.value?.objectId)
                    }
                    name="category"
                    helperText={
                      formik.touched.category &&
                      (formik.errors.category as string)
                    }
                    error={
                      !!(formik.touched.category && formik.errors.category)
                    }
                    onBlur={formik.handleBlur}
                    multiple={false}
                  />
                </Grid>
                <Grid item xs={12} lg={12}>
                  <TextField
                    id="description"
                    multiline
                    fullWidth
                    rows={4}
                    label="กรอกรายละเอียดเพิ่มเติม"
                    name="description"
                    helperText={
                      formik.touched.description &&
                      (formik.errors.description as string)
                    }
                    error={
                      !!(
                        formik.touched.description && formik.errors.description
                      )
                    }
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.description}
                  />
                </Grid>
                <Grid item xs={12} lg={12}>
                  <TextField
                    id="commission"
                    fullWidth
                    label="ค่าคอมมิชชั่น(ถ้ามี)"
                    name="commission"
                    onBlur={formik.handleBlur}
                    value={formik.values.commission}
                    type={'number'}
                    onChange={(e) => {
                      // const cost = Math.max(0, Number(e.target.value) || 0)
                      formik.setFieldValue('commission', e.target.value)
                    }}
                  />
                </Grid>
                {isAllowTikTok(user.merchant) && (
                  <Grid item xs={12} lg={12}>
                    <ChannelSelectMultipleSearch
                      // resetAutoComplete={resetAutoComplete}
                      selected={formik.values.channels?.map(
                        (channel: any) => channel.objectId,
                      )}
                      label={'เลือกช่องทางการขาย'}
                      onSelect={(e: any) => {
                        const channels = e?.map((item: any) => {
                          return {
                            name: item.value.name,
                            objectId: item.value.objectId,
                            code: item.value?.ref || '',
                            image: item.image,
                          }
                        })
                        formik.setFieldValue('channels', channels)
                      }}
                      multiple={true}
                      name="channels"
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                )}
                <Grid item xs={12} lg={12}>
                  <Grid container spacing={2} mt={1}>
                    {options.map((option, index: number) => {
                      return (
                        <Grid
                          container
                          key={index}
                          spacing={2}
                          mt={1}
                          alignItems={'center'}
                          justifyContent={'center'}
                        >
                          <Grid item xs={10} lg={10}>
                            <TextField
                              required
                              id={option.name}
                              name={option.name}
                              fullWidth
                              label={'ชื่อสินค้า'}
                              value={option.name}
                              onChange={(e) =>
                                changeValueByIndexOptions(index, {
                                  name: e.target.value,
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={2} lg={2}>
                            <IconButton
                              onClick={() => handleDeleteOptionByIndex(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                          <Grid item xs={12} lg={4}>
                            <TextField
                              id={option.size}
                              name={option.size}
                              fullWidth
                              label={'ขนาดหรือปริมาตร (ถ้ามี)'}
                              value={option.size}
                              onChange={(e) =>
                                changeValueByIndexOptions(index, {
                                  size: e.target.value,
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={12} lg={4}>
                            <TextField
                              id={option.weight}
                              name={option.weight}
                              fullWidth
                              label={'น้ำหนัก (ถ้ามี)'}
                              value={option.weight}
                              onChange={(e) =>
                                changeValueByIndexOptions(index, {
                                  weight: e.target.value,
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={12} lg={4}>
                            <TextField
                              id={option.color}
                              name={option.color}
                              fullWidth
                              label={'สี (ถ้ามี)'}
                              value={option.color}
                              onChange={(e) =>
                                changeValueByIndexOptions(index, {
                                  color: e.target.value,
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={12} lg={12}>
                            <InputUpload
                              width="30%"
                              handleUploadClick={(e) =>
                                handleUploadImg(index, e)
                              }
                              imageUrl={option.image ?? ''}
                            />
                          </Grid>
                          <Grid item xs={12} lg={12}>
                            <hr style={{ opacity: '.4' }} />
                          </Grid>
                          {/*<Grid item xs={12} lg={12} alignItems={'center'}>*/}
                          {/*  <img src={option.image}*/}
                          {/*       style={{ marginTop: '20px', maxWidth: '100%', height: 'auto' }} />*/}
                          {/*</Grid>*/}
                        </Grid>
                      )
                    })}
                  </Grid>
                </Grid>
                {!isUpdate && (
                  <Grid item xs={12} lg={4}>
                    <Button
                      startIcon={<AddIcon />}
                      color="primary"
                      size="medium"
                      variant="contained"
                      onClick={() => {
                        setOptions([
                          ...options,
                          {
                            name: '',
                            image: '',
                            size: '',
                            weight: '',
                            color: '',
                          },
                        ])
                      }}
                    >
                      เพิ่มสินค้าหลายไซส์
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={12}>
                  <TextField
                    id="code"
                    fullWidth
                    label="รหัสสินค้า"
                    name="code"
                    helperText={
                      formik.touched.code && (formik.errors.code as string)
                    }
                    error={!!(formik.touched.code && formik.errors.code)}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.code}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <TextField
                    id="price"
                    fullWidth
                    disabled={collections.length > 0}
                    label="ราคาขาย"
                    name="price"
                    helperText={
                      formik.touched.price && (formik.errors.price as string)
                    }
                    error={!!(formik.touched.price && formik.errors.price)}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.setFieldValue('price', e.target.value)
                    }}
                    value={formik.values.price}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <TextField
                    id="cost"
                    fullWidth
                    disabled={collections.length > 0}
                    label="ราคาทุน"
                    name="cost"
                    helperText={
                      formik.touched.cost && (formik.errors.cost as string)
                    }
                    error={!!(formik.touched.cost && formik.errors.cost)}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.setFieldValue('cost', e.target.value)
                    }}
                    value={formik.values.cost}
                  />
                </Grid>
                {!product && (
                  <Grid item xs={12} lg={4}>
                    <TextField
                      fullWidth
                      id="quantity"
                      label="จำนวน(ถ้ามี)"
                      name="quantity"
                      onBlur={formik.handleBlur}
                      onChange={(e) => {
                        const quantity = Math.max(
                          0,
                          Number(e.target.value) || 0,
                        )
                        formik.setFieldValue('quantity', quantity)
                      }}
                      value={formik.values.quantity}
                    />
                  </Grid>
                )}
                <Grid item xs={12} lg={4}>
                  <TextField
                    fullWidth
                    id="size"
                    label="ขนาดหรือปริมาตร (ถ้ามี)"
                    name="size"
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.setFieldValue('size', e.target.value)
                    }}
                    value={formik.values.size}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <TextField
                    fullWidth
                    id="weight"
                    label="น้ำหนัก (ถ้ามี)"
                    name="weight"
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.setFieldValue('weight', e.target.value)
                    }}
                    value={formik.values.weight}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <TextField
                    fullWidth
                    id="color"
                    label="สี (ถ้ามี)"
                    name="color"
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.setFieldValue('color', e.target.value)
                    }}
                    value={formik.values.color}
                  />
                </Grid>
                <Grid item xs={12} lg={12}>
                  <InputUpload
                    width="30%"
                    handleUploadClick={handleUploadClick}
                    imageUrl={selectedImg ?? ''}
                  />
                </Grid>
                <Grid item xs={12} lg={12}>
                  <label>เปิดการใช้งาน</label>
                  <Switch
                    checked={isAvailable}
                    onChange={() => setIsAvailable(!isAvailable)}
                  />
                </Grid>
                {/* {!isUpdate && (
                  <Grid item xs={12} lg={12}>
                    <label>tiktok</label>
                    <Switch
                      checked={formik.values.tiktok?.isTiktok ?? false}
                      onChange={() =>
                        formik.setFieldValue(
                          'tiktok.isTiktok',
                          !formik.values.tiktok?.isTiktok,
                        )
                      }
                    />
                  </Grid>
                )} */}
                {/* {formik.values.tiktok?.isTiktok && (
                  <>
                    <Grid item xs={12} lg={12}>
                      <TextField
                        id="tiktokWeight"
                        fullWidth
                        label="น้ำหนักสินค้า(กรัม)"
                        name="tiktokWeight"
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          const weight = Math.max(
                            0,
                            Number(e.target.value) || 0,
                          )
                          formik.setFieldValue('tiktok.tiktokWeight', weight)
                        }}
                        value={formik.values.tiktok?.tiktokWeight}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <SelectSearch<any>
                        label={'เลือกหมวดหมู่ Tiktok'}
                        onSelect={(e) =>
                          handleSlectTiktokCategory(e?.value?.id)
                        }
                        search={searchTiktokCategory}
                        onSearch={handleSearchTiktokCategory}
                        options={
                          tikTokCategoriesList?.data?.map(
                            (category: any) =>
                              category && {
                                label: category.name,
                                value: category,
                              },
                          ) || ([] as IOptionSelect<any>[])
                        }
                        name="category"
                        helperText={
                          formik.touched.category &&
                          (formik.errors.category as string)
                        }
                        error={
                          !!(formik.touched.category && formik.errors.category)
                        }
                        onBlur={formik.handleBlur}
                      />
                    </Grid>
                    {tiktokIsSizeChartRequired &&
                      formik.values.tiktok?.tiktokCategoryId && (
                        <Grid item xs={12} lg={12}>
                          <InputUpload
                            width="30%"
                            handleUploadClick={(e) =>
                              tiktokSizeChartImagehandleUploadClick(e)
                            }
                            imageUrl={
                              formik.values.tiktok?.tiktokSizeChartImage ?? ''
                            }
                          />
                        </Grid>
                      )}
                  </>
                )} */}
                <Grid item xs={12} lg={12}>
                  <Grid container spacing={2} mt={1}>
                    {attributes.map((attribute, index: number) => {
                      return (
                        <Grid
                          container
                          key={index}
                          spacing={2}
                          mt={1}
                          alignItems={'center'}
                          justifyContent={'center'}
                        >
                          <Grid item xs={10} lg={10}>
                            <TextField
                              required
                              id={attribute.name}
                              name={attribute.name}
                              fullWidth
                              label={'ชื่อคุณสมบัติสินค้า'}
                              value={attribute.name}
                              onChange={(e) =>
                                changeValueByIndexAttributes(index, {
                                  name: e.target.value,
                                })
                              }
                            />
                            <TextField
                              sx={{ marginTop: '10px' }}
                              required
                              id={attribute.value}
                              name={attribute.value}
                              fullWidth
                              label={'ค่าคุณสมบัติสินค้า'}
                              value={attribute.value}
                              onChange={(e) =>
                                changeValueByIndexAttributes(index, {
                                  value: e.target.value,
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={2} lg={2}>
                            <IconButton
                              onClick={() =>
                                handleDeleteAttributeByIndex(index)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Grid>
                {/* <Grid item xs={12} lg={4}>
                  <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    size="medium"
                    variant="contained"
                    onClick={() => {
                      setAttributes([
                        ...attributes,
                        {
                          name: '',
                          value: '',
                        },
                      ])
                    }}
                  >
                    เพิ่มคุณสมบัติสินค้า
                  </Button>
                </Grid> */}
              </Grid>
            </Grid>
          </Grid>
          <Typography mt={4} mb={2} variant={`h6`}>
            สร้างสินค้าเซต
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={12}>
              {/* <SelectSearch<IProduct>
                label={'เลือกสินค้า'}
                onSelect={(e) => e && handleAddCollection(e?.value!)}
                search={searchProduct}
                onSearch={handleSearchProduct}
                options={
                  productList?.data?.records
                    .map(
                      (product: IProduct) =>
                        product.isAvailable && {
                          label: `${product.name} (${product.code})`,
                          image: product.image,
                          value: product,
                        },
                    )
                    .reduce((acc: IOptionSelect<IProduct>[], product) => {
                      if (product) {
                        acc.push(product)
                      }
                      return acc
                    }, []) || ([] as IOptionSelect<IProduct>[])
                }
              /> */}
              <ProductSelectSearch
                multiple={false}
                onSelect={(e) => e && handleAddCollection(e?.value!)}
              />
            </Grid>
            {collections.length > 0 && (
              <Grid container>
                <Grid item lg={12}>
                  <DialogTitle
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    รายการ เซตสินค้าที่เลือก
                    <Box>
                      <label>กำหนดราคาต้นทุนสัมพันธ์กับจำนวนสินค้า</label>
                      <Switch
                        checked={isCostCollections}
                        onChange={() =>
                          handleSetCostCollections(!isCostCollections)
                        }
                      />
                    </Box>
                  </DialogTitle>
                </Grid>
                <Grid item lg={12}>
                  <Table
                    columns={columns}
                    dataSource={collections?.map(
                      (product: ICollections, index: number) => {
                        return {
                          ...product,
                          key: index,
                        }
                      },
                    )}
                    bordered
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                {product
                  ? product.isCopy
                    ? 'คัดลอกสินค้า'
                    : 'แก้ไขสินค้า'
                  : 'เพิ่มสินค้าใหม่'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
